import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import moment from 'moment'

export interface FocusSeshLogItem {
  id: number | null
  completed: boolean | null
  secondsDuration: number | null
  isAutoCheckIns: boolean | null
  checkInTimingLowerLimit: number | null
  checkInTimingUpperLimit: number | null
  numCheckIns: number | null
  avgCheckInResponseValue: number | null
  createdAt: string | null
  linkedClassroomId: number | null
  isSyncedSesh: boolean | null
  discarded?: boolean
}

export const selectFocusSeshLogs = (state: any) => state.focusSeshLogs
export const selectCompletedFocusSeshLogs = (state: any) => state.focusSeshLogs.filter((log: FocusSeshLogItem) => !log.discarded && (log.completed || log.isSyncedSesh || log.avgCheckInResponseValue))
export const selectCompletedFocusSeshLogsWithAverageFocus = (state: any) => state.focusSeshLogs.filter((log: FocusSeshLogItem) => log.avgCheckInResponseValue && !log.discarded)
export const selectCompletedFocusSeshLogsAfterMomentDateTime = (momentDateTime: moment.Moment) => (state: any) => state.focusSeshLogs.filter((log: FocusSeshLogItem) => log.completed && moment(log.createdAt).isAfter(momentDateTime))
export const selectHasCompletedFocusSeshWithAutoCheckIns = (state: any) => state.focusSeshLogs.some((log: FocusSeshLogItem) => log.completed && log.isAutoCheckIns)
export const selectHasStartedAnySession = (state: any) => state.focusSeshLogs.some((log: FocusSeshLogItem) => !!log.id)
export const selectHasStartedPersonalSession = (state: any) => state.focusSeshLogs.some((log: FocusSeshLogItem) => !!log.id && log.isSyncedSesh === false)

const initialState: FocusSeshLogItem[] = [{
  id: null,
  completed: null,
  secondsDuration: null,
  isAutoCheckIns: null,
  checkInTimingLowerLimit: null,
  checkInTimingUpperLimit: null,
  numCheckIns: null,
  avgCheckInResponseValue: null,
  createdAt: null,
  linkedClassroomId: null,
  isSyncedSesh: null,
}]

const focusSeshLogsSlice = createSlice({
  name: 'focusSeshLog',
  initialState,
  reducers: {
    loadFocusSeshLogs: (state, {
      payload: focusSeshLogs,
    }: PayloadAction<FocusSeshLogItem[]>) => focusSeshLogs,
    addFocusSeshLog: (state, {
      payload: focusSeshLog,
    }: PayloadAction<FocusSeshLogItem>) => {
      // if log already exists in state, update log, otherwise add it
      const existingLogIndex = state.findIndex((log) => log.id === focusSeshLog.id)
      if (existingLogIndex === -1) {
        state.push(focusSeshLog)
      } else {
        state[existingLogIndex] = focusSeshLog
      }
    },
    // Replaces the logs in state with a freshly pulled set from the server, but keeps the
    // local version of the session that is currently being completed. Sessions logged from
    // another client (a projected session run from a teacher's client, for example) are not
    // in state until they are pulled, which would otherwise leave the end of session
    // screens calculating achievements from stale data. The in progress session is kept
    // because the server may not have been updated with its final values yet.
    refreshPreviousFocusSeshLogs: (state, {
      payload: { focusSeshLogs, preserveLogId },
    }: PayloadAction<{ focusSeshLogs: FocusSeshLogItem[]; preserveLogId: number | null }>) => {
      const inProgressLog = preserveLogId !== null
        ? state.find((log) => log.id === preserveLogId)
        : undefined

      if (!inProgressLog) return focusSeshLogs

      // Keep the in progress log last so that it stays the most recent log in state
      return [
        ...focusSeshLogs.filter((log) => log.id !== preserveLogId),
        { ...inProgressLog },
      ]
    },
    updateFocusSeshLog: (state, { payload: { id, updates } }: PayloadAction<{ id: number; updates: Partial<FocusSeshLogItem> }>) => {
      const index = state.findIndex((log) => log.id === id)
      if (index !== -1) {
        state[index] = { ...state[index], ...updates }
      }
    },
  },
})

export const {
  loadFocusSeshLogs,
  addFocusSeshLog,
  refreshPreviousFocusSeshLogs,
  updateFocusSeshLog,
} = focusSeshLogsSlice.actions

export default focusSeshLogsSlice.reducer
